import React, { useState } from "react";
import {
  Platform,
  View,
  TouchableOpacity,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedButton } from "@/components/ThemedButton";
import { MediaCaptureModal } from "@/components/media/MediaCaptureModal";
import {
  type ICapturedGeoLocation,
  getHighAccuracyLocation,
  generateCaptureFileName,
} from "@/services/media-capture";
import { trackMediaCaptured } from "@/services/analytics";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import * as ImagePicker from "expo-image-picker";

/**
 * Properties for the standalone CapturePhoto component.
 */
export interface ICapturePhotoProps {
  /** Callback triggered when a photo is captured */
  onCapture: (file: File | any, location?: ICapturedGeoLocation | null) => Promise<void> | void;
  /** Whether the capture action is disabled */
  disabled?: boolean;
  /** Whether an upload is currently processing */
  uploading?: boolean;
  /** Button title label (defaults to "Photo") */
  title?: string;
  /** Button style variant */
  variant?: "primary" | "secondary" | "minimal" | "danger";
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Optional text style override */
  textStyle?: StyleProp<TextStyle>;
  /** Initial camera facing type (defaults to "back") */
  initialCameraType?: "back" | "front";
  /** Whether to render an inline camera flip toggle button beside the trigger */
  showFlipButton?: boolean;
  /** Custom trigger render function */
  renderTrigger?: (
    onPress: () => void,
    disabled: boolean,
    toggleCameraType?: () => void,
    cameraType?: "back" | "front"
  ) => React.ReactNode;
}

/**
 * Standalone Photo Capture component supporting high-accuracy geolocation tagging,
 * camera hardware permissions, web viewfinder modal, native camera integration,
 * and front/rear camera orientation selection.
 *
 * @param {ICapturePhotoProps} props - Capture photo properties.
 * @returns {React.JSX.Element} The rendered CapturePhoto component.
 */
export function CapturePhoto({
  onCapture,
  disabled = false,
  uploading = false,
  title = "Photo",
  variant = "secondary",
  style,
  textStyle,
  initialCameraType = "back",
  showFlipButton = false,
  renderTrigger,
}: ICapturePhotoProps): React.JSX.Element {
  const [modalVisible, setModalVisible] = useState(false);
  const [cameraType, setCameraType] = useState<"back" | "front">(initialCameraType);
  const tintColor = useThemeColor({}, "tint");
  const headerBgColor = useThemeColor({}, "buttonSecondary");
  const borderColor = useThemeColor({}, "border");

  const isDisabled = disabled || uploading;

  /**
   * Toggles camera facing mode between front and rear.
   */
  const toggleCameraType = (): void => {
    if (isDisabled) return;
    setCameraType((prev) => (prev === "back" ? "front" : "back"));
  };

  /**
   * Triggers photo capture across platforms.
   */
  const handleTrigger = async (): Promise<void> => {
    if (isDisabled) return;

    if (Platform.OS === "web") {
      setModalVisible(true);
      return;
    }

    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== ImagePicker.PermissionStatus.GRANTED && (status as any) !== "granted") {
        CustomAlert.alert("Permission Required", "Please grant camera permission to take a photo.");
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"],
        quality: 1.0,
        cameraType: cameraType === "front" ? ImagePicker.CameraType.front : ImagePicker.CameraType.back,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];
      const location = await getHighAccuracyLocation();
      trackMediaCaptured("photo", undefined, Boolean(location));
      const fileName = asset.fileName || generateCaptureFileName("photo", asset.mimeType || "image/jpeg");
      const fileItem = {
        name: fileName,
        type: asset.mimeType || "image/jpeg",
        size: asset.fileSize || 0,
        uri: asset.uri,
        file: (asset as any).file,
        exif: asset.exif,
        width: asset.width,
        height: asset.height,
      };

      await onCapture(fileItem as any, location);
    } catch (err: any) {
      console.error("[CapturePhoto] Native photo capture error:", err);
      CustomAlert.alert("Capture Error", err.message || "Failed to take photo.");
    }
  };

  const handleCaptureComplete = (file: File | any, location?: ICapturedGeoLocation | null): void => {
    trackMediaCaptured("photo", undefined, Boolean(location));
    setModalVisible(false);
    onCapture(file, location);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleTrigger, isDisabled, toggleCameraType, cameraType)
      ) : showFlipButton ? (
        <View style={styles.controlGroup}>
          <ThemedButton
            title={title}
            icon={<Ionicons name="camera-outline" size={16} color={tintColor} />}
            variant={variant}
            disabled={isDisabled}
            onPress={handleTrigger}
            style={[
              { backgroundColor: headerBgColor, borderColor },
              style,
              styles.mainButton,
            ]}
            textStyle={textStyle}
            accessibilityLabel={`Take Photo (${cameraType === "front" ? "Front Camera" : "Rear Camera"})`}
            accessibilityRole="button"
          />
          <TouchableOpacity
            onPress={toggleCameraType}
            disabled={isDisabled}
            style={[
              styles.flipButton,
              { backgroundColor: headerBgColor, borderColor },
              isDisabled && styles.disabledButton,
            ]}
            accessibilityLabel="Turn camera around"
            accessibilityRole="button"
            accessibilityHint={`Toggle camera orientation (currently ${cameraType})`}
          >
            <Ionicons name="camera-reverse-outline" size={16} color={tintColor} />
          </TouchableOpacity>
        </View>
      ) : (
        <ThemedButton
          title={title}
          icon={<Ionicons name="camera-outline" size={16} color={tintColor} />}
          variant={variant}
          disabled={isDisabled}
          onPress={handleTrigger}
          style={[
            { backgroundColor: headerBgColor, borderColor },
            style,
          ]}
          textStyle={textStyle}
          accessibilityLabel={`Take Photo (${cameraType === "front" ? "Front Camera" : "Rear Camera"})`}
          accessibilityRole="button"
        />
      )}

      {/* Web Viewfinder Modal with Camera Flip Support */}
      <MediaCaptureModal
        mode={modalVisible ? "photo" : null}
        initialFacingMode={cameraType === "front" ? "user" : "environment"}
        onClose={() => setModalVisible(false)}
        onCaptureComplete={handleCaptureComplete}
      />
    </>
  );
}

export default CapturePhoto;

const styles = StyleSheet.create({
  controlGroup: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  mainButton: {
    borderRightWidth: 0,
  },
  flipButton: {
    paddingHorizontal: 8,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 0,
    minHeight: 38,
  },
  disabledButton: {
    opacity: 0.5,
  },
});
